/**
 * dunningAccess — geteilte Zugriffslogik für das Mahnwesen (M2, Take-away-Gate
 * Pricing-Repackaging PR-T5, architecture-pricing-repackaging.md §3.3).
 *
 * Single source of truth dafür, WER die Mahn-UI (Modal, Policy-Settings,
 * History-Sub-Rows, „Mahnung erstellen"-Row-Action) im OP-Dashboard sehen und
 * benutzen darf. Ersetzt die früheren per-User-Inline-`DUNNING_ENABLED`-Checks
 * im Render-/Logik-Pfad von `OpenItemsPage.tsx` (der MODUL-Scope —
 * `const DUNNING_ENABLED` + die lazy-`require`-Bindings der Komponenten — bleibt
 * bewusst auf dem reinen Build-Flag `REACT_APP_DUNNING_ENABLED`: dort gibt es
 * keine `sellerData`, und der isolatedModules-Lazy-Require-Pfad muss unangetastet
 * bleiben).
 *
 * KRITISCHE Null-Delta-Invariante (Mahnwesen ist LIVE): solange `TIER_V2_FLAG`
 * `false` ist, liefert `canUseDunning` exakt `DUNNING_FLAG` — d. h. das heutige
 * IST-Verhalten, byte-identisch zum bisherigen `DUNNING_ENABLED`. Erst wenn der
 * Master-Flag aktiv wird (PR-T6-Aktivierung), greift die Premium-/
 * Grandfathering-Gate: Premium-Tier ODER Bestandsschutz (`grandfatheredFeatures`
 * enthält `'dunning'`) behält Zugriff; alle anderen verlieren ihn (Take-away).
 *
 * Firebase-frei (nur `process.env` + pure `entitlements`-Funktionen) — aus
 * `OpenItemsPage.tsx` und `config/navigation.ts` importierbar.
 *
 * Im **Advisor-Kontext** (Steuerberater im Mandanten-Tenant) trägt das Gate
 * KEINEN Owner-Tier-Term: ein StB darf NIE durch den fehlenden Premium-Status
 * seines eigenen (Kanzlei-)Kontos aus den Mandanten-Flächen ausgesperrt werden
 * (M-A1 / architecture-grace-window.md §6.3, T4b-Muster wie
 * `datevExportAccess.ts`). Das Build-Flag dominiert weiterhin — ein Advisor
 * sieht NIE ein nicht-ausgeliefertes Feature.
 *
 * @module config/dunningAccess
 */

import {
  resolveTier,
  hasMinTier,
  isGrandfathered,
  TIER_V2_FLAG,
  type SellerLike,
} from './entitlements';
import { canUseBankReconcile } from './bankReconcileAccess';

/**
 * Mahnwesen-Build-Flag (M2). Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet — identisch zum
 * `DUNNING_ENABLED`-Konstanten-Ausdruck im Modul-Scope von `OpenItemsPage.tsx`
 * (dieselbe Env-Var). LIVE `true`.
 */
export const DUNNING_FLAG: boolean = process.env.REACT_APP_DUNNING_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer die Mahn-UI sehen/benutzen darf.
 *
 * Reihenfolge (Take-away-Gate §3.3, Advisor-Bypass M-A1 §6.3):
 *   1. Build-Flag aus (`!DUNNING_FLAG`) → nie (Feature nicht ausgeliefert) —
 *      dominiert VOR dem Advisor-Bypass (StB sieht nie Unausgeliefertes).
 *   2. Advisor-Kontext ⇒ `true` (KEIN Owner-Tier-Term — ein StB wird nie durch
 *      den Tier seines eigenen Kontos aus Mandanten-Flächen gesperrt, §6.3).
 *   3. Master-Flag aus (`!TIER_V2_FLAG`) → immer (IST-Verhalten, Null-Delta —
 *      byte-identisch zum bisherigen `DUNNING_ENABLED`).
 *   4. Sonst: Premium-Tier ODER Grandfathering für `'dunning'`.
 *
 * @param sellerData - Roh-Seller-Doc-Daten aus `useAuth().sellerData` (oder
 *   `null`, solange nicht geladen). Fail-closed bei `null`/Nicht-Objekt.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *   Mandanten-Kontext ist (`!isOwnScope` / readOnly, §6.3) — NICHT
 *   `useIsAdvisorContext()` (eigener Kanzlei-Scope = Owner-Pfad). Dann KEIN
 *   Owner-Tier-Term (aber Build-Flag bleibt bindend).
 * @returns `true`, wenn der Nutzer das Mahnwesen nutzen darf.
 */
export function canUseDunning(
  sellerData: SellerLike | null | undefined,
  isAdvisorContext: boolean,
): boolean {
  if (!DUNNING_FLAG) {
    return false;
  }
  // §6.3 Advisor-Kontext: kein Owner-Tier-Term, StB nie aussperren.
  if (isAdvisorContext) {
    return true;
  }
  if (!TIER_V2_FLAG) {
    return true;
  }
  return (
    hasMinTier(resolveTier(sellerData), 'premium') ||
    isGrandfathered(sellerData, 'dunning')
  );
}

/**
 * Halbautomatik-Build-Flag (MA-5, architecture-mahn-automatik.md §5.6/§9).
 * Fail-closed: nur bei explizitem `'true'` aktiv. Modul-Konstante, einmalig
 * beim Bundle-Load ausgewertet.
 */
export const DUNNING_SEMIAUTO_FLAG: boolean =
  process.env.REACT_APP_DUNNING_SEMIAUTO_ENABLED === 'true';

/**
 * Beta-Erprobung Mahn-Halbautomatik (Vorschlags-Panel im OP-Dashboard) —
 * hartkodiert (Muster `RECEIPT_SCAN_BETA_UIDS`), für die kontrollierte
 * Live-Erprobung durch Test- (b3DRR1…) und Business-Account (70Tgnu…).
 *
 * WICHTIG (bewusste Zwischenstufe, KEIN Code-Bug): Das Panel wird für diese UIDs
 * sichtbar, ist aber NICHT voll funktionsfähig, bis die Firestore-Rules für
 * `dunning_invoice_settings` und `dunning_level_locks` deployt sind — der Loader
 * läuft sonst fail-closed (permission-denied ⇒ alle Zeilen pausiert). Der
 * Rules-Deploy ist derzeit extern blockiert (StB-Consent-Backfill). Vor
 * allgemeiner Freigabe wieder leeren.
 */
export const DUNNING_SEMIAUTO_BETA_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
  '70TgnuHwmDNI2LovagtcxXtIflo2',
];

/**
 * Vollautomatik-Build-Flag (MA-5, §5.6/§9). Fail-closed: nur bei explizitem
 * `'true'` aktiv.
 */
export const DUNNING_AUTO_FLAG: boolean =
  process.env.REACT_APP_DUNNING_AUTO_ENABLED === 'true';

/**
 * Beta-Erprobung Vollautomatik (O-2) — ENV-VAR-getrieben, exakt das Muster
 * von {@link BANK_RECONCILE_BETA_UIDS} (`bankReconcileAccess.ts`), NICHT
 * hartkodiert. Leer, solange die Env-Var nicht gesetzt ist (fail-closed).
 *
 * Bypass-Umfang bewusst ENGER als beim Bank-Abgleich-Vorbild: eine
 * Beta-UID überspringt NUR den {@link DUNNING_AUTO_FLAG}-Check (frühen
 * GA-Zugriff auf den AUTO-Modus), nicht aber die darunterliegende
 * {@link canUseDunningAutomation}-Kette (Mahnwesen-Zugriff ∧
 * Bank-Abgleich/Ultra-Tier ∧ Halbautomatik-Flag). Vollautomatik ohne
 * Coverage-Gate-Grundlage (Bank-Abgleich) wäre nutzlos und funktional
 * gar nicht auswertbar (E9) — ein Beta-Flag darf diese Grundvoraussetzung
 * nicht aushebeln.
 */
export const DUNNING_AUTO_BETA_UIDS: readonly string[] = (
  process.env.REACT_APP_DUNNING_AUTO_BETA_UIDS ?? ''
)
  .split(',')
  .map((uid) => uid.trim())
  .filter((uid) => uid.length > 0);

/**
 * Entscheidet fail-closed, ob ein Nutzer die Halbautomatik (Vorschlags-Panel
 * + Freigabe-Flow) sehen/benutzen darf (§5.6).
 *
 * Komposition: `canUseDunning(sellerData) ∧ canUseBankReconcile(uid, tier) ∧
 * DUNNING_SEMIAUTO_FLAG`. Tier=Ultra (E9) steckt vollständig in
 * `canUseBankReconcile` — diese Funktion trifft keine eigene Tier-Aussage.
 *
 * **Abweichung von der Architektur-Kurzformel (dokumentiert, siehe
 * changelog-mahn-automatik.md):** §5.6 schreibt `canUseDunning(sellerData)`
 * einargumentig; die reale (unveränderte) Signatur verlangt zusätzlich
 * `isAdvisorContext: boolean`. Diese Funktion ruft `canUseDunning(sellerData,
 * false)` — die Automatik ist an den Ultra-Tier/Bank-Abgleich-Status des
 * jeweils übergebenen `sellerData` gebunden, nicht an einen impliziten
 * Advisor-Bypass; die konservativere (fail-closed) Variante. Sichtbarkeit für
 * Steuerberater:innen im Mandanten-Kontext bleibt dadurch NICHT
 * automatisch versperrt — der Aufrufer entscheidet über das übergebene
 * `sellerData` (Mandanten-Doc), Mutationen werden ohnehin zusätzlich per
 * `useIsReadOnly()` in der UI-Schicht deaktiviert (§7.7/§8.1a, MA-6/MA-10 —
 * NICHT Teil dieser Funktion).
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`, solange nicht
 *   geladen). Fail-closed bei `null`/Nicht-Objekt.
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder
 *   `null`/`undefined`, solange nicht eingeloggt). Fail-closed bei fehlender
 *   UID (Bank-Abgleich-Beta-Allowlist kann dann nicht greifen).
 * @returns `true`, wenn der Nutzer die Halbautomatik nutzen darf.
 */
export function canUseDunningAutomation(
  sellerData: SellerLike | null | undefined,
  uid: string | null | undefined,
): boolean {
  // Beta-Erprobung (§9): eine Beta-UID überspringt NUR den Halbautomatik-Flag-
  // Check (frühen Zugriff aufs Vorschlags-Panel), MUSS aber weiterhin die
  // darunterliegende Kette (Mahnwesen-Zugriff ∧ Bank-Abgleich/Ultra-Tier)
  // erfüllen — analog `DUNNING_AUTO_BETA_UIDS`. Die Beta-UIDs stehen ohnehin auf
  // der Bank-Abgleich-Beta-Allowlist (`canUseBankReconcile`), sind also für die
  // Coverage-Grundlage berechtigt.
  const semiautoAllowed =
    DUNNING_SEMIAUTO_FLAG || (uid != null && DUNNING_SEMIAUTO_BETA_UIDS.includes(uid));
  if (!semiautoAllowed) {
    return false;
  }
  if (!canUseDunning(sellerData, false)) {
    return false;
  }
  return canUseBankReconcile(uid, resolveTier(sellerData));
}

/**
 * Entscheidet fail-closed, ob ein Nutzer die Vollautomatik-Settings-UI
 * (Modus `'auto'` wählbar, Opt-in-Dialog) sehen/benutzen darf (§5.6).
 *
 * Komposition: `canUseDunningAutomation(sellerData, uid) ∧
 * (DUNNING_AUTO_FLAG ∨ Beta-Allowlist)` — Beta-UIDs (O-2) erhalten frühen
 * Zugriff auf den AUTO-Modus, MÜSSEN aber weiterhin die Halbautomatik-Kette
 * (Mahnwesen-Zugriff ∧ Ultra-Tier/Bank-Abgleich ∧ Halbautomatik-Flag)
 * erfüllen — siehe {@link DUNNING_AUTO_BETA_UIDS}.
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`, solange nicht
 *   geladen). Fail-closed bei `null`/Nicht-Objekt.
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder
 *   `null`/`undefined`, solange nicht eingeloggt).
 * @returns `true`, wenn der Nutzer die Vollautomatik-Settings-UI nutzen darf.
 */
export function canUseDunningFullAuto(
  sellerData: SellerLike | null | undefined,
  uid: string | null | undefined,
): boolean {
  if (!canUseDunningAutomation(sellerData, uid)) {
    return false;
  }
  if (uid && DUNNING_AUTO_BETA_UIDS.includes(uid)) {
    return true;
  }
  return DUNNING_AUTO_FLAG;
}
